import React, {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Storage} from "aws-amplify";
import config from "configuration/settings.js";

import {useTheme} from "connectui/theme/ThemeProvider";
import {buildStyles} from "connectui/component/buildStyles";
import ICONS from "connectui/icons/iconMap";

import FlexContainer from "connectui/component/layout/FlexContainer";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import SvgIcon from "connectui/component/display/SvgIcon";
import Divider from "connectui/component/display/Divider";
import ButtonIconText from "connectui/component/input/button/ButtonIconText";
import OutlinedButtonBase from "connectui/component/input/button/base/OutlinedButtonBase";
import ContextMenu from "connectui/component/input/context/ContextMenu";

import useResizeObserver from "hooks/useResizeObserver";
import {useImageCompressor} from "utilities/fileUtilities";
import {asyncLoop, newUuid, sleep} from "utilities/helperFunctions";
import {useFeatureFlags} from "utilities/featureUtil";
import {
  closeGeneralError,
  openGeneralError,
} from "redux/actions/settingsActions";
import usePrevious from "hooks/usePrevious";
import Container from "connectui/component/layout/Container";
import LoadingBar from "connectui/component/display/LoadingBar";

const useStyles = (theme) =>
  buildStyles({
    row: {
      width: "100%",
      padding: theme.spacing(0, 4),
    },
    sendIcon: {
      width: "20px",
      height: "20px",
    },
    imgContainer: {
      width: "50px",
      height: "50px",
      borderRadius: `${theme.radius.sm}px`,
      position: "relative",
      cursor: "pointer",
      "&:hover .x": {
        width: "18px",
        height: "18px",
        opacity: 1,
      },
    },
    xIcon: {
      width: 0,
      height: 0,
      opacity: 0,
      borderRadius: "50%",
      backgroundColor: theme.colors.white,
      boxShadow: theme.shadows.sm,
      position: "absolute",
      cursor: "pointer",
      top: "-9px",
      right: "-9px",
      transition: "opacity 0.15s",
      padding: theme.spacing(0.5),
    },
    img: {
      objectFit: "cover",
      width: "50px",
      height: "50px",
      borderRadius: `${theme.radius.sm}px`,
    },
  });

function CircleButton({icon, iconProps, onClick}) {
  const {theme} = useTheme();
  const classes = useStyles(theme);

  return (
    <ButtonIcon
      icon={
        <SvgIcon
          component={ICONS[icon]}
          sx={{
            width: "20px",
            height: "20px",
            color: theme.colors.gray600,
          }}
          {...iconProps}
        />
      }
      sx={{
        backgroundColor: theme.colors.gray100,
        width: "28px",
        height: "28px",
        borderRadius: "50%",
        "&:hover": {backgroundColor: theme.colors.gray200},
      }}
      onClick={onClick}
    />
  );
}

const ActionsRow = ({
  guestId,
  bpLink,
  disableAttachments,
  disableImages,
  insertText,
  attachedImages,
  setAttachedImages,
  sendMessage,
  saveDisabled,
  onTemplatesOpen,
  onScheduleOpen,
  sendButtonSpacing = false,
}) => {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);
  const {cannedResponses} = useFeatureFlags();
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const imgInputRef = useRef(null);
  const currentUser = useSelector((state) => state.defaultReducer.current_user);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [compressEnabled, setCompressEnabled] = useState(false);
  const [loaderOpen, setLoaderOpen] = useState(false);
  const [progressObj, setProgressObj] = useState({loaded: 0, total: 100});
  const prevGuestId = usePrevious(guestId);
  const {width} = useResizeObserver(containerRef);
  const isCompactView = width < 330;

  useImageCompressor({
    srcImgs:
      !!compressEnabled && filesToUpload[0] === "upload"
        ? filesToUpload.slice(1)
        : [],
    onSuccess: (outputImgs) => onCompressSuccess(outputImgs),
  });

  useEffect(() => {
    if (prevGuestId !== guestId) {
      if (attachedImages.length) {
        // Remove attachments when switching conversations
        removeImages(attachedImages);
      }
      setAttachedImages([]);
    }
  }, [guestId, attachedImages]);

  useEffect(() => {
    if (!filesToUpload.length) return;
    if (filesToUpload[0] === "upload") {
      // Start compression
      sleep(250).then(() => {
        setLoaderOpen(true);
        setCompressEnabled((prev) => true);
      });
    }
  }, [filesToUpload]);

  const onCompressSuccess = (outputImgs) => {
    setCompressEnabled(false);
    setFilesToUpload([]);
    handleUploadImages(outputImgs);
  };

  // Upload images to S3
  const handleUploadImages = async (files) => {
    const newLinks = [];
    const totalProgress = {
      current: 0,
      totalSize: files.reduce((acc, curr) => acc + curr.size, 0),
    };
    setProgressObj((prev) => ({loaded: 0, total: totalProgress.totalSize}));

    await asyncLoop(files, async (item, index) => {
      const newId = newUuid();
      const imageName = `${currentUser}/messages/${guestId}/attachment-${newId}`;
      if (index > 0) {
        totalProgress.current += files[index - 1].size;
      }
      await Storage.put(imageName, item, {
        contentType: item.type,
        progressCallback(progress) {
          setProgressObj((prev) => ({
            ...prev,
            loaded: totalProgress.current + progress.loaded,
          }));
        },
      });
      newLinks.push({
        id: newId,
        url: `${config.s3.URL}${imageName}`,
        name: imageName,
      });
    });

    setAttachedImages((prev) => prev.concat(newLinks));
    setLoaderOpen(false);
  };

  // Remove unused attachments
  const removeImages = (imgs) => {
    if (imgs.length) {
      for (let index = 0; index < imgs.length; index++) {
        const img = imgs[index];
        Storage.remove(img.name);
      }
    }
  };

  const handleError = (errorText) => {
    dispatch(
      openGeneralError({
        open: true,
        message: "Failed to attach image",
        disableSeverity: true,
        subtitle: errorText,
        onClose: () => dispatch(closeGeneralError()),
      }),
    );
  };

  const validateFile = (file) => {
    return new Promise((resolve) => {
      const re = /^image\/.+$/;
      if (!re.test(file.type)) {
        handleError("Invalid file. Only images are allowed.");
      } else {
        setFilesToUpload((prev) => [...prev, file]);
      }
      resolve(true);
    });
  };

  const loadImages = async (files) => {
    // Prepare files to upload
    await asyncLoop(files, async (f) => {
      await validateFile(f);
    });

    // Files ready to upload
    setFilesToUpload((prev) => {
      if (!prev.length) return prev;
      else return ["upload", ...prev];
    });
    setProgressObj({loaded: 0, total: 100});
  };

  const openTemplates = () => {
    onTemplatesOpen();
  };

  const closeMenu = () => setAnchorEl(null);

  return (
    <>
      <ContextMenu
        menuItems={[
          {
            label: "Boarding Pass link",
            icon: ICONS["boarding-pass-ticket"],
            onClick: () => {
              insertText(bpLink);
              closeMenu();
            },
          },
          disableImages
            ? undefined
            : {
                label: "Image",
                icon: ICONS.image,
                onClick: () => {
                  imgInputRef.current.click();
                  closeMenu();
                },
              },
        ]}
        open={!!anchorEl}
        onClose={closeMenu}
        anchorEl={anchorEl}
      />
      {loaderOpen && (
        <Container
          style={{
            width: "100%",
            padding: theme.spacing(0, 4),
            marginBottom: theme.spacing(1),
          }}
        >
          <LoadingBar
            progress={Math.ceil((progressObj.loaded * 100) / progressObj.total)}
          />
        </Container>
      )}
      {!!attachedImages.length && !loaderOpen && (
        <FlexContainer
          alignItems="center"
          gap={theme.spacing(2)}
          sx={{
            width: "100%",
            overflow: "auto hidden",
            padding: theme.spacing(2.5, 4, 0.5),
          }}
        >
          {attachedImages.map((img, i) => (
            <Container key={img.id} className={classes.imgContainer}>
              <SvgIcon
                component={ICONS["small-x"]}
                className={`${classes.xIcon} x`}
                onClick={() => {
                  setAttachedImages((prev) =>
                    prev.filter((attached) => attached.id !== img.id),
                  );
                  removeImages([img]);
                }}
              />
              <img
                key={img.id}
                src={img.url}
                alt={`Attachment ${i + 1}`}
                className={classes.img}
              />
            </Container>
          ))}
        </FlexContainer>
      )}
      <FlexContainer
        ref={containerRef}
        className={classes.row}
        alignItems="center"
        justifyContent="space-between"
      >
        <input
          id="image-attachment"
          ref={imgInputRef}
          style={{display: "none"}}
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => loadImages(e.target.files)}
        />
        <FlexContainer alignItems="center" gap={theme.spacing(2)}>
          {!disableAttachments && (
            <ButtonIcon
              icon={
                <SvgIcon
                  component={ICONS.plus}
                  sx={{
                    width: "20px",
                    height: "20px",
                    color: theme.colors.gray600,
                  }}
                />
              }
              sx={{
                backgroundColor: theme.colors.gray100,
                width: "28px",
                height: "28px",
                padding: theme.spacing(1.5),
                borderRadius: "50%",
                "&:hover": {backgroundColor: theme.colors.gray200},
              }}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            />
          )}
          {!disableAttachments && cannedResponses && (
            <Divider orientation="vertical" sx={{height: "14px", margin: 0}} />
          )}
          {cannedResponses && (
            <ButtonIconText
              label={isCompactView ? "" : "Templates"}
              weight="medium"
              color="gray700"
              typographyVariant="textSM"
              sx={{
                color: theme.colors.gray600,
                backgroundColor: theme.colors.gray100,
                borderRadius: theme.spacing(4),
                padding: isCompactView ? theme.spacing(2) : theme.spacing(1, 3),
                "&:hover": {backgroundColor: theme.colors.gray200},
              }}
              icon={
                <SvgIcon
                  component={ICONS["dash-square"]}
                  sx={{
                    marginRight: isCompactView ? 0 : theme.spacing(1),
                    width: "14px",
                    height: "14px",
                    color: theme.colors.gray600,
                  }}
                />
              }
              onClick={openTemplates}
            />
          )}
        </FlexContainer>
        <FlexContainer
          alignItems="center"
          gap={theme.spacing(2)}
          sx={{
            marginRight: theme.spacing(sendButtonSpacing && !isMobile ? 15 : 0),
          }}
        >
          <ButtonIconText
            label={isCompactView ? "" : "Schedule"}
            weight="semibold"
            typographyVariant="textSM"
            buttonBase={OutlinedButtonBase}
            sx={{padding: theme.spacing(2)}}
            icon={
              <SvgIcon
                component={ICONS["send-scheduled"]}
                className={classes.sendIcon}
                sx={{marginRight: isCompactView ? 0 : theme.spacing(2)}}
              />
            }
            onClick={onScheduleOpen}
          />
          <ButtonIcon
            disabled={saveDisabled}
            sx={{padding: theme.spacing(2)}}
            icon={
              <SvgIcon component={ICONS.send} className={classes.sendIcon} />
            }
            onClick={sendMessage}
          />
        </FlexContainer>
      </FlexContainer>
    </>
  );
};

export default ActionsRow;
